import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  HttpLink,
  InMemoryCache,
  ServerError,
} from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { getGraphqlEndpoint } from '@/shared/lib/graphql-endpoint';
import { useAuthStore } from '@/app/auth/stores/auth.store';
import { storeRedirectTarget } from '@/app/auth/lib/redirect';

const getAccessToken = (): string | undefined => useAuthStore.getState().session?.access_token;

const authLink = new ApolloLink((operation, forward) => {
  const token = getAccessToken();

  if (token) {
    operation.setContext(({ headers }: { headers?: Record<string, string> }) => ({
      headers: {
        ...headers,
        authorization: `Bearer ${token}`,
      },
    }));
  }

  return forward(operation);
});

const errorLink = new ErrorLink(({ error }) => {
  const { session, clearSession } = useAuthStore.getState();
  if (!session) {
    return;
  }

  const unauthorized = ServerError.is(error)
    ? error.statusCode === 401
    : CombinedGraphQLErrors.is(error) &&
      error.errors.some((gqlError) => {
        const ext = gqlError.extensions as
          | { response?: { status?: unknown }; statusCode?: unknown }
          | undefined;
        const status = ext?.response?.status ?? ext?.statusCode;

        return status === 401;
      });

  if (unauthorized) {
    storeRedirectTarget(window.location.pathname + window.location.search);
    clearSession();
    window.location.assign('/login');
  }
});

const buildLink = () =>
  errorLink.concat(
    authLink.concat(
      new HttpLink({
        uri: getGraphqlEndpoint(),
      }),
    ),
  );

export const repointApolloLink = (): void => {
  apolloClient.setLink(buildLink());
};

const listFieldPolicy = <T>() => ({
  keyArgs: false as const,
  merge(_existing: readonly T[] | undefined, incoming: readonly T[]) {
    return incoming;
  },
});

export const apolloClient = new ApolloClient({
  link: buildLink(),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          listDevices: listFieldPolicy(),
          listDeviceGatewayAssignments: listFieldPolicy(),
          listDeviceRssi: listFieldPolicy(),
        },
      },
      Device: {
        fields: {
          meta: {
            merge(
              existing: Record<string, unknown> | undefined,
              incoming: Record<string, unknown>,
            ) {
              return {
                ...existing,
                ...incoming,
              };
            },
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
});
